import type { SyncResponse } from "@trader/shared/CandlesApi"
import { DISPLAY_TIME_ZONE } from "../../ui/displayTime.ts"
import type { ToastTone } from "../../ui/toasts.ts"

// The chart's Sync button (docs/design/0010) asks the server to fetch the
// latest bars for its sync list now; this words the outcome for a toast.

export interface SyncNotice {
  readonly tone: ToastTone
  readonly title: string
  readonly detail?: string
}

const clock = new Intl.DateTimeFormat("en-GB", { timeZone: DISPLAY_TIME_ZONE, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })

/** What a sync did, in one toast: errors first, then how much arrived and how recent it is. */
export const syncNotice = (response: SyncResponse): SyncNotice => {
  if (response.alreadyRunning) {
    return {
      tone: "info",
      title: "A sync is already running",
      detail: "The first sync downloads years of 1-minute history and can take about half an hour. Charts fill in as it goes."
    }
  }
  const failed = response.results.filter((result) => result.error !== null)
  if (failed.length > 0) {
    const reasons = [...new Set(failed.map((result) => result.error))].join("; ")
    return { tone: "error", title: `Sync failed for ${failed.length} of ${response.results.length} series`, detail: reasons }
  }
  const instruments = new Set(response.results.map((result) => result.instrument)).size
  const bars = response.results.reduce((sum, result) => sum + result.bars, 0)
  const newest = Math.max(...response.results.map((result) => result.to ?? 0))
  return {
    tone: "ok",
    title: `Synced ${instruments} instrument${instruments === 1 ? "" : "s"}`,
    detail: `${bars.toLocaleString("en")} bars written${newest > 0 ? `; up to ${clock.format(newest * 1000)}` : ""}.`
  }
}
